import React, { useEffect, useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { styled } from "@mui/material/styles";
import funnel from "../styles/Funnel.module.css";
import { NumericFormat } from "react-number-format";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import WhereToVoteIcon from "@mui/icons-material/WhereToVote";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import CardContent from "@mui/material/CardContent";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MuiToggleButton from "@mui/material/ToggleButton";
import Link from "next/link";
import Footer from "../component/footer";
import CallToAction from "../component/callToAction";

const ToggleButton = styled(MuiToggleButton)({
  "&.Mui-selected, &.Mui-selected:hover": {
    color: "white",
    backgroundColor: "var(--primary-color)",
  },
});

const CssTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: "var(--primary-color)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "var(--primary-color)",
    },
  },
});

const SelectButton = styled(Link)(({ theme }) => ({
  color: "#fff",
  border: "1px solid var(--primary-color)",
  fontWeight: "600",
  width: "100%",
  height: "45px",
  fontSize: "16px",
  backgroundColor: "var(--primary-color)",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
  marginTop: "15px",
  "&:hover": {
    border: "1px solid var(--primary-color)",
    backgroundColor: "var(--dark-color)",
  },
}));

const SearchButton = styled(Button)(({ theme }) => ({
  color: "#fff",
  border: "1px solid var(--primary-color)",
  fontWeight: "600",
  width: "180px",
  height: "40px",
  fontSize: "15px",
  backgroundColor: "var(--primary-color)",
  borderRadius: "8px",
  "&:hover": {
    border: "1px solid var(--primary-color)",
    backgroundColor: "var(--dark-color)",
  },
  [theme.breakpoints.down("sm")]: {
    width: "150px",
  },
}));

const Item = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

let CustomSelect = styled(Select)(() => ({
  "&.MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: "var(--primary-color)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "var(--primary-color)",
    },
  },
}));

export default function EquityCalculator() {
  const [formData, setFormData] = React.useState({
    estimated: "",
    outstanding: "",
    term: "",
    rate: "",
  });

  const [error, setError] = React.useState({
    estimated: false,
    outstanding: false,
    term: false,
    rate: false,
  });

  const [equity, setEquity] = React.useState(false);
  const [show, setShow] = React.useState(false);

  const handleChangeEstimated = (event) => {
    setFormData({
      ...formData,
      estimated: event.target.value,
    });
    setError({
      ...error,
      estimated: false,
    });
  };

  let errorEstimated;
  let helperTextEstimated;
  if (error.estimated == true) {
    errorEstimated = "error";
    helperTextEstimated = "Estimated property value cannot be empty!";
  } else {
    errorEstimated = "";
    helperTextEstimated = "";
  }

  const handleChangeOutstanding = (event) => {
    setFormData({
      ...formData,
      outstanding: event.target.value,
    });
    setError({
      ...error,
      outstanding: false,
    });
  };

  let errorOutstanding;
  let helperTextOutstanding;
  if (error.outstanding == true) {
    errorOutstanding = "error";
    helperTextOutstanding = "Outstanding loan amount cannot be empty!";
  } else {
    errorOutstanding = "";
    helperTextOutstanding = "";
  }

  const handleChangeRate = (event) => {
    setFormData({
      ...formData,
      rate: event.target.value,
    });
    setError({
      ...error,
      rate: false,
    });
  };

  let errorRate;
  let helperTextRate;
  if (error.rate == true) {
    errorRate = "error";
    helperTextRate = "Interest rate value cannot be empty!";
  } else {
    errorRate = "";
    helperTextRate = "";
  }

  const handleChangeTerm = (event) => {
    setFormData({
      ...formData,
      term: event.target.value,
    });
    setError({
      ...error,
      term: false,
    });
  };

  let errorTerm;
  let helperTextTerm;
  if (error.term == true) {
    errorTerm = "error";
    helperTextTerm = "Loan term value cannot be empty!";
  } else {
    errorTerm = "";
    helperTextTerm = "";
  }

  const calculateButton = () => {
    if (formData.estimated === "" || formData.estimated.length <= 0) {
      setError({
        ...error,
        estimated: true,
      });
    } else if (
      formData.outstanding === "" ||
      formData.outstanding.length <= 0
    ) {
      setError({
        ...error,
        outstanding: true,
      });
    } else if (formData.rate === "" || formData.rate.length <= 0) {
      setError({
        ...error,
        rate: true,
      });
    } else if (formData.term === "" || formData.term.length <= 0) {
      setError({
        ...error,
        term: true,
      });
    } else if (
      error.estimated == false &&
      error.outstanding == false &&
      error.rate == false &&
      error.term == false
    ) {
      setError({
        ...error,
        estimated: false,
        outstanding: false,
        rate: false,
        term: false,
      });
      let propertyValue = formData.estimated.replaceAll(",", "");
      propertyValue = parseInt(propertyValue);
      let loanAmount = formData.outstanding.replaceAll(",", "");
      loanAmount = parseInt(loanAmount);
      let interestRate = parseFloat(formData.rate);
      let loanTerm = parseInt(formData.term);

      let equity = 0.8 * propertyValue - loanAmount;

      let monthlyInterestRate = interestRate / 12;
      let totalPayments = loanTerm * 12;
      let loanBalance =
        (loanAmount *
          (Math.pow(1 + monthlyInterestRate, totalPayments) -
            Math.pow(1 + monthlyInterestRate, totalPayments - 1))) /
        (Math.pow(1 + monthlyInterestRate, totalPayments) - 1);
      setEquity(equity.toLocaleString());
      setShow(true);
    }
  };

  return (
    <>
      <Head>
        <title>Equity calculator</title>
        <link
          rel="canonical"
          href="https://abodefinder.com.au/equity_calculator/"
        ></link>
        <meta name="description" content="description" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/png" sizes="32x32" href="https://abodefinder.com.au/blog-static/img/favicon-32.ico"/>
      </Head>
      <Container sx={{ flexGrow: 12, mt: 3, mb: 10 }}>
        <Container sx={{ textAlign: "center" }}>
          <Typography
            variant="h1"
            sx={{ fontSize: "25px", fontWeight: "600", mt: 8, mb: 2 }}
          >
            Equity Calculator - Determine Your Home Equity
          </Typography>
          <Typography
            variant="h2"
            sx={{ fontSize: "22px", fontWeight: "600", mb: 3 }}
          >
            Unlock the Potential of Your Home with Our Equity Calculator
          </Typography>
          <Typography variant="subtitle2" sx={{ fontSize: "18px" }}>
            AbodeFinder's Equity Calculator is a powerful tool that allows you
            to determine the equity available in your home. Equity represents
            the difference between the current value of your home and the
            outstanding balance on your mortgage. By using our calculator, you
            can determine the equity available to you and explore your options
            for accessing it.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontSize: "18px" }}>
            Whether you're looking to renovate your home, consolidate debt, or
            invest in property, equity can be a valuable resource. With our
            user-friendly and intuitive Equity Calculator, you can quickly and
            easily determine the equity available in your home and make informed
            decisions about your financial future.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontSize: "18px" }}>
            Simply enter your property details and outstanding mortgage balance,
            and our calculator will do the rest. Our advanced algorithms take
            into account the current market value of your property and provide
            you with an accurate estimate of your available equity.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontSize: "18px", mb: 4 }}>
            At AbodeFinder, we believe in empowering our clients to take control
            of their finances and make informed decisions about their
            investments. Our Equity Calculator is just one of the many tools we
            offer to help you achieve your financial goals. Explore our website
            today to discover more.
          </Typography>
        </Container>
        <Grid container spacing={1}>
          <Grid item xs={12} md={12} spacing={2} columns={{ xs: 12 }}>
            <Item
              variant="outlined"
              sx={(theme) => ({
                p: 3,
                pt: 3,
                mt: 2,
                mb: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-left",
                borderRadius: "4px",
                [theme.breakpoints.down("sm")]: { p: 4 },
              })}
            >
              <Box
                sx={(theme) => ({
                  width: "100%",
                  mt: 3,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  [theme.breakpoints.down("sm")]: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  },
                })}
              >
                <div className={funnel.FunnelFieldCal}>
                  <h4 className={funnel.h4TextFunnel}>
                    Estimated property value
                  </h4>
                  <FormControl fullWidth>
                    <NumericFormat
                      error={errorEstimated}
                      helperText={helperTextEstimated}
                      value={formData.estimated}
                      name="Estimated"
                      placeholder="950,000"
                      onChange={handleChangeEstimated}
                      id="outlined-adornment-amount"
                      thousandSeparator=","
                      customInput={CssTextField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </div>
                <div className={funnel.FunnelFieldCal}>
                  <h4 className={funnel.h4TextFunnel}>
                    Outstanding loan amount
                  </h4>
                  <FormControl fullWidth>
                    <NumericFormat
                      error={errorOutstanding}
                      helperText={helperTextOutstanding}
                      value={formData.outstanding}
                      name="Outstanding"
                      placeholder="450,000"
                      onChange={handleChangeOutstanding}
                      id="outlined-adornment-amount"
                      thousandSeparator=","
                      customInput={CssTextField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </div>
              </Box>
              <Box
                sx={(theme) => ({
                  width: "100%",
                  mt: 1,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  [theme.breakpoints.down("sm")]: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  },
                })}
              >
                <div className={funnel.FunnelFieldCal}>
                  <h4 className={funnel.h4TextFunnel}>Loan term</h4>
                  <FormControl fullWidth>
                    <NumericFormat
                      error={errorTerm}
                      helperText={helperTextTerm}
                      value={formData.term}
                      name="Loan term"
                      placeholder="30"
                      onChange={handleChangeTerm}
                      id="outlined-adornment-amount"
                      customInput={CssTextField}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">years</InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </div>
                <div className={funnel.FunnelFieldCal}>
                  <h4 className={funnel.h4TextFunnel}>Interest rate</h4>
                  <FormControl fullWidth>
                    <NumericFormat
                      error={errorRate}
                      helperText={helperTextRate}
                      value={formData.rate}
                      name="Interest rate"
                      placeholder="2.09"
                      onChange={handleChangeRate}
                      id="outlined-adornment-amount"
                      customInput={CssTextField}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </div>
              </Box>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SearchButton
                  variant="contained"
                  sx={(theme) => ({
                    mt: 1,
                    ml: 1,
                    width: "300px",
                    fontSize: "20px",
                    height: "50px",
                    [theme.breakpoints.down("sm")]: { mt: 4, ml: 0, mb: 3 },
                  })}
                  onClick={calculateButton}
                >
                  Calculate
                </SearchButton>
              </Box>
            </Item>
          </Grid>
        </Grid>
        {show && (
          <Grid container spacing={1}>
            <React.Fragment>
              <Grid item xs={12} md={12} columns={{ xs: 12 }}>
                <Paper
                  sx={{
                    p: 5,
                    mt: 5,
                    borderColor: "var(--primary-color)",
                    textAlign: "center",
                  }}
                  variant="outlined"
                >
                  <Typography variant="h4" gutterBottom>
                    Accessible cash
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{ fontWeight: "600" }}
                    gutterBottom
                  >
                    $ {equity}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    You could potentially access more, however Lenders Mortgage
                    Insurance may apply.
                  </Typography>
                </Paper>
              </Grid>
            </React.Fragment>
          </Grid>
        )}
        <CallToAction />
        <Item
          variant="outlined"
          sx={(theme) => ({
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "center",
            p: 2,
            mb: 5,
            [theme.breakpoints.down("sm")]: {
              flexDirection: "column",
              width: "100%",
            },
          })}
        >
          <Item
            variant="outlined"
            sx={(theme) => ({
              width: "48%",
              height: "310px",
              mr: 2,
              mt: 1,
              mb: 1,
              [theme.breakpoints.down("md")]: { width: "100%", height: "auto" },
            })}
          >
            <CardContent
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Avatar
                sx={{
                  width: 75,
                  height: 75,
                  backgroundColor: "var(--primary-color)",
                }}
              >
                <WhereToVoteIcon sx={{ fontSize: "50px" }} />
              </Avatar>
              <Typography
                variant="subtitle1"
                sx={{ textAlign: "center", mt: 3 }}
              >
                Find the perfect suburb for your next property investment with
                SuburbFinder. Get customised recommendations based on your
                budget, public transport needs, and school preferences.
              </Typography>
              <SelectButton href="/suburb-finder/">
                Find Your Ideal Suburb Now
              </SelectButton>
            </CardContent>
          </Item>
          <Item
            variant="outlined"
            sx={(theme) => ({
              width: "48%",
              height: "310px",
              mr: 2,
              mt: 1,
              mb: 1,
              [theme.breakpoints.down("md")]: { width: "100%", height: "auto" },
            })}
          >
            <CardContent
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Avatar
                sx={{
                  width: 75,
                  height: 75,
                  backgroundColor: "var(--primary-color)",
                }}
              >
                <QueryStatsIcon sx={{ fontSize: "50px" }} />
              </Avatar>
              <Typography
                variant="subtitle1"
                sx={{ textAlign: "center", mt: 3 }}
              >
                Calculate your chances of buying the perfect property with our
                AI-powered Buying Chance Calculator. Get personalized insights
                based on your budget and selected suburbs.
              </Typography>
              <SelectButton href="/buying-chance/">
                Calculate Your Buying Chance Now
              </SelectButton>
            </CardContent>
          </Item>
        </Item>
      </Container>
      <Footer />
    </>
  );
}
